import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getRemembered, saveRemembered, clearRemembered } from "../../utils/audioPreferences";
import ModePreviewButton from "./ModePreviewButton";

/**
 * RECLAIM — Universal Audio Entry
 * --------------------------------------------------------------------
 * The single, universal "enter a state" experience used across the
 * entire app (Challenges, Presence, Programs, Night, Performance,
 * Healing, Vision Board, Manifestation).
 *
 * First visit:
 *   Title → "Choose how you'd like to experience this…"
 *   3 stacked glass buttons (Clear / Soft ✦ / Deep)
 *   Optional "Remember my preference" toggle
 *   Tap a mode → soft press + glow + onSelect(mode)
 *
 * Repeat visit (when remembered):
 *   Title → "Continuing in <Mode>"
 *   ONE big tap target → onSelect(mode)  (keeps iOS gesture intact)
 *   Small "Choose different mode" link → falls back to 3-button selector
 *
 * Defaults per area:
 *   - Most areas: Soft
 *   - Night: Deep
 *   - Instruction-heavy: Clear
 */

export const RECLAIM_MODES = [
  {
    id: "clear",
    label: "Clear",
    description: "Hear the guidance fully",
    accent: "#E5DFCE",   // warm silver
    glow:   "rgba(229,223,206,0.30)",
  },
  {
    id: "soft",
    label: "Soft",
    description: "Immersive, balanced experience",
    accent: "#CBA153",   // gold
    glow:   "rgba(203,161,83,0.40)",
    recommended: true,
  },
  {
    id: "deep",
    label: "Deep",
    description: "Subtle, subconscious integration",
    accent: "#8B92F2",   // soft indigo
    glow:   "rgba(139,146,242,0.38)",
  },
];

const modeMeta = (id) => RECLAIM_MODES.find((m) => m.id === id) || RECLAIM_MODES[1];

export default function AudioEntry({
  title,
  subtitle,
  selected,                 // optional controlled value
  defaultMode = "soft",     // per-area default
  area,                     // string — used as key for "Remember my preference"
  onSelect,                 // (modeId) => void   — called after tap (post-press animation)
  disabled = false,
  busy = false,             // show subtle loading shimmer (e.g. while music is warming)
  busyLabel = "Preparing your state…",
  rememberable = true,      // show the "Remember my preference" toggle
}) {
  const [activeMode, setActiveMode] = useState(selected || defaultMode);
  const [pressedMode, setPressedMode] = useState(null);
  const [remember, setRemember] = useState(false);
  const [rememberedView, setRememberedView] = useState(null); // { mode } when in single-button mode

  // Sync external selected
  useEffect(() => {
    if (selected && selected !== activeMode) setActiveMode(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // On mount, check if a per-area remembered preference exists
  useEffect(() => {
    if (!rememberable || !area) return;
    const saved = getRemembered(area);
    if (saved?.remember && saved?.mode) {
      setRememberedView({ mode: saved.mode });
      setActiveMode(saved.mode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area]);

  const fireSelect = (id) => {
    if (rememberable && area) saveRemembered(area, id, remember || rememberedView != null);
    onSelect?.(id);
  };

  const handleTap = (id) => {
    if (disabled || pressedMode) return;
    setActiveMode(id);
    setPressedMode(id);
    // Allow soft-press animation (~320ms) before firing onSelect → fade into session
    setTimeout(() => {
      fireSelect(id);
    }, 320);
  };

  const handleResumeRemembered = () => {
    if (!rememberedView?.mode || pressedMode) return;
    setPressedMode(rememberedView.mode);
    setTimeout(() => {
      onSelect?.(rememberedView.mode);
    }, 320);
  };

  const handleChangeMode = () => {
    if (!area) return;
    clearRemembered(area);
    setRememberedView(null);
    setRemember(false);
  };

  const isRememberedOnly = !!rememberedView && !pressedMode;
  const m = isRememberedOnly ? modeMeta(rememberedView.mode) : null;

  return (
    <div
      className="relative w-full mx-auto"
      style={{ maxWidth: 440 }}
      data-testid="audio-entry"
    >
      {/* Title */}
      {title && (
        <h2
          className="font-heading text-2xl sm:text-3xl text-white text-center mb-2"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
        >
          {title}
        </h2>
      )}
      {subtitle && (
        <p className="text-white/45 text-sm text-center mb-6">{subtitle}</p>
      )}

      <AnimatePresence mode="wait" initial={false}>
        {isRememberedOnly ? (
          /* ─── Single-button mode (remembered preference) ─────────────── */
          <motion.div
            key="remembered"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <p
              className="text-center text-white/65 mb-7 italic"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 15 }}
            >
              Continuing in <span style={{ color: m.accent }}>{m.label}</span>
            </p>

            <motion.button
              type="button"
              onClick={handleResumeRemembered}
              disabled={disabled || pressedMode}
              data-testid="audio-entry-resume"
              whileTap={{ scale: 0.98 }}
              animate={
                pressedMode
                  ? { scale: [1, 1.035, 1] }
                  : { scale: 1 }
              }
              transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full text-left rounded-2xl px-5 py-7"
              style={{
                background: `linear-gradient(135deg, ${m.accent}28 0%, rgba(255,255,255,0.02) 100%)`,
                border: `1px solid ${m.accent}66`,
                boxShadow: `0 0 36px ${m.glow}, inset 0 0 0 1px ${m.accent}22`,
                backdropFilter: "blur(18px)",
                WebkitBackdropFilter: "blur(18px)",
                cursor: disabled || pressedMode ? "default" : "pointer",
              }}
            >
              <div className="flex items-baseline justify-between gap-3">
                <p
                  className="text-white"
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: 24,
                    fontWeight: 400,
                    color: m.accent,
                  }}
                >
                  Begin in {m.label}
                </p>
                {/* Preview the remembered mode without committing */}
                <ModePreviewButton mode={m.id} accent={m.accent} />
              </div>
              <p
                className="text-sm mt-1"
                style={{ color: "rgba(255,255,255,0.55)" }}
              >
                {m.description}
              </p>
            </motion.button>

            <button
              type="button"
              onClick={handleChangeMode}
              disabled={pressedMode}
              data-testid="audio-entry-change"
              className="block mx-auto mt-4 text-[12px] tracking-[0.16em] uppercase text-white/45 hover:text-white/70 transition-colors"
            >
              Choose different mode
            </button>

            <p className="text-[11px] text-white/35 text-center mt-5 italic">
              {busy ? busyLabel : "You can change this anytime"}
            </p>
          </motion.div>
        ) : (
          /* ─── Three-button mode ─────────────────────────────────────── */
          <motion.div
            key="three-button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <p
              className="text-center text-white/65 mb-7 italic"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 15 }}
            >
              Choose how you’d like to experience this…
            </p>

            <div className="flex flex-col gap-3">
              {RECLAIM_MODES.map((mm, idx) => {
                const isActive = activeMode === mm.id && !pressedMode;
                const isPressed = pressedMode === mm.id;
                const isRec = !!mm.recommended;

                return (
                  <motion.button
                    key={mm.id}
                    type="button"
                    onClick={() => handleTap(mm.id)}
                    disabled={disabled || !!pressedMode}
                    data-testid={`audio-entry-${mm.id}`}
                    initial={{ opacity: 0, y: 14 }}
                    animate={
                      isPressed
                        ? { scale: [1, 1.035, 1], opacity: 1, y: 0 }
                        : { opacity: 1, y: 0, scale: 1 }
                    }
                    transition={{
                      delay: pressedMode ? 0 : idx * 0.08,
                      duration: isPressed ? 0.55 : 0.7,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    whileHover={!disabled && !pressedMode ? { y: -2 } : {}}
                    whileTap={{ scale: 0.98 }}
                    className="relative w-full text-left rounded-2xl px-5 transition-colors"
                    style={{
                      paddingTop: isRec ? 22 : 18,
                      paddingBottom: isRec ? 22 : 18,
                      background:
                        isActive || isPressed
                          ? `linear-gradient(135deg, ${mm.accent}26 0%, rgba(255,255,255,0.02) 100%)`
                          : "rgba(255,255,255,0.04)",
                      border: `1px solid ${
                        isActive || isPressed ? mm.accent + "66" : "rgba(255,255,255,0.07)"
                      }`,
                      boxShadow:
                        isActive || isPressed
                          ? `0 0 36px ${mm.glow}, inset 0 0 0 1px ${mm.accent}22`
                          : "none",
                      backdropFilter: "blur(18px)",
                      WebkitBackdropFilter: "blur(18px)",
                      cursor: disabled || pressedMode ? "default" : "pointer",
                    }}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <p
                        className="text-white"
                        style={{
                          fontFamily: "'Playfair Display', serif",
                          fontSize: isRec ? 22 : 19,
                          fontWeight: 400,
                          color: isActive || isPressed ? mm.accent : "rgba(255,255,255,0.95)",
                          transition: "color 350ms ease",
                        }}
                      >
                        {mm.label}
                      </p>
                      <div className="flex items-center gap-2">
                        {isRec && (
                          <span
                            className="text-[9px] uppercase tracking-[0.22em]"
                            style={{ color: mm.accent, opacity: 0.85 }}
                          >
                            ✦ recommended
                          </span>
                        )}
                        {/* Tap-to-hear a 3-second sample at this mode's volumes */}
                        <ModePreviewButton mode={mm.id} accent={mm.accent} />
                      </div>
                    </div>
                    <p
                      className="text-sm mt-1"
                      style={{ color: "rgba(255,255,255,0.55)" }}
                    >
                      {mm.description}
                    </p>

                    {/* Pressed-state shimmer ring */}
                    {isPressed && (
                      <motion.span
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: [0.6, 0], scale: [1, 1.06] }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        className="absolute inset-0 rounded-2xl pointer-events-none"
                        style={{ boxShadow: `0 0 60px ${mm.glow}` }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* Remember my preference */}
            {rememberable && area && (
              <button
                type="button"
                onClick={() => setRemember((r) => !r)}
                disabled={pressedMode}
                data-testid="audio-entry-remember"
                aria-pressed={remember}
                className="flex items-center gap-2.5 mx-auto mt-6 px-4 py-2 rounded-full transition-colors"
                style={{
                  background: remember ? "rgba(203,161,83,0.10)" : "rgba(255,255,255,0.03)",
                  border: `1px solid ${remember ? "rgba(203,161,83,0.45)" : "rgba(255,255,255,0.08)"}`,
                }}
              >
                {/* Toggle dot */}
                <span
                  className="relative inline-block w-9 h-5 rounded-full transition-colors"
                  style={{ background: remember ? "rgba(203,161,83,0.45)" : "rgba(255,255,255,0.12)" }}
                >
                  <motion.span
                    layout
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    className="absolute top-0.5 w-4 h-4 rounded-full"
                    style={{
                      left: remember ? 18 : 2,
                      background: remember ? "#F5EBC4" : "rgba(255,255,255,0.6)",
                      boxShadow: remember ? "0 0 8px rgba(245,235,196,0.6)" : "none",
                    }}
                  />
                </span>
                <span
                  className="text-[12px] tracking-wide"
                  style={{ color: remember ? "#F5EBC4" : "rgba(255,255,255,0.55)" }}
                >
                  Remember my preference
                </span>
              </button>
            )}

            {/* Micro-copy */}
            <p className="text-[11px] text-white/35 text-center mt-4 italic">
              {busy ? busyLabel : "You can change this anytime"}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
